import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {useIntl} from "react-intl";
import messages from "../../i18n/messages";

const RocketCountdownGame = () => {
    const [slots, setSlots] = useState(Array(10).fill(null));
    const [numbers, setNumbers] = useState(shuffle([...Array(10).keys()].map(i => i + 1).reverse()));
    const [rocketLaunched, setRocketLaunched] = useState(false);
    const intl  = useIntl();

    // 打乱数组顺序
    function shuffle(array) {
        return array.sort(() => Math.random() - 0.5);
    }

    const handleDragStart = (e, number, isSlot) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ number, isSlot }));
    };

    const handleDrop = (e, index) => {
        e.preventDefault();
        const data = JSON.parse(e.dataTransfer.getData('text/plain'));
        const { number, isSlot } = data;

        const newSlots = [...slots];
        const newNumbers = [...numbers];

        if (newSlots[index] !== null) {
            // 如果目标空格已有数字，将其移动到可拖动区域
            newNumbers.push(newSlots[index]);
        }

        if (isSlot) {
            // 如果是从空格中拖动的数字，先将其从原位置移除
            newSlots[slots.indexOf(number)] = null;
        } else {
            // 如果是从数字区域拖动的数字，将其从数字区域移除
            newNumbers.splice(numbers.indexOf(number), 1);
        }

        // 将新数字放入目标空格
        newSlots[index] = number;
        setSlots(newSlots);
        setNumbers(newNumbers);

        checkCompletion(newSlots);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const checkCompletion = (slots) => {
        const isCorrect = slots.every((slot, index) => slot === 10 - index);
        if (isCorrect) {
            setRocketLaunched(true);
        }
    };

    const resetGame = () => {
        setSlots(Array(10).fill(null));
        setNumbers(shuffle([...Array(10).keys()].map(i => i + 1).reverse()));
        setRocketLaunched(false);
    };

    return (
        <GameContainer>
            <h1>{intl.formatMessage(messages.rocketCountdownGameLabel)}</h1>
            <h3>{intl.formatMessage(messages.rocketCountdownGameInfo)}</h3>
            <Slots>
                {slots.map((slot, index) => (
                    <Slot
                        key={index}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragOver={handleDragOver}
                    >
                        {slot !== null && (
                            <Number
                                draggable
                                onDragStart={(e) => handleDragStart(e, slot, true)}
                            >
                                {slot}
                            </Number>
                        )}
                    </Slot>
                ))}
            </Slots>
            <Numbers>
                {numbers.map((number) => (
                    <Number
                        key={number}
                        draggable
                        onDragStart={(e) => handleDragStart(e, number, false)}
                    >
                        {number}
                    </Number>
                ))}
            </Numbers>
            <RocketContainer>
                <LaunchPad />
                <MechanicalArm rocketLaunched={rocketLaunched} />
                <RocketLaunch rocketLaunched={rocketLaunched}>
                    <RocketBody>
                        <RocketNose />
                        <RocketMiddle />
                        <RocketEngine />
                        <RightFrontFins />
                        <RightRearFins />
                        <LeftFrontFins />
                        <LeftRearFins />
                    </RocketBody>
                    {rocketLaunched && <Fire />}
                </RocketLaunch>
            </RocketContainer>
            {rocketLaunched && <ResetButton onClick={resetGame}>{intl.formatMessage(messages.gamePlayAgain)}</ResetButton>}
        </GameContainer>
    );
};

// Styled Components
const GameContainer = styled.div`
  text-align: center;
  font-family: Arial, sans-serif;
`;

const Slots = styled.div`
    display: flex;
    flex-wrap: wrap; /* 允许换行 */
    justify-content: center; /* 居中排列 */
    gap: 10px; /* 空格之间的间距 */
    margin-bottom: 20px;
`;

const Slot = styled.div`
  width: 50px;
  height: 50px;
  border: 2px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 5px;
  font-size: 24px;
  background-color: #f0f0f0;
`;

const Numbers = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
`;

const Number = styled.div`
  width: 50px;
  height: 50px;
  border: 2px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background-color: #e0e0e0;
  cursor: grab;
`;

const RocketContainer = styled.div`
  position: relative;
  width: 200px;
  height: 400px;
  margin: 0 auto;
`;

const LaunchPad = styled.div`
  width: 20px;
  height: 100px;
  background-color: #555;
  position: absolute;
  bottom: 0;
  left: 0;
`;

const MechanicalArm = styled.div`
  width: 10px;
  height: 30px;
  background-color: #777;
  position: absolute;
  bottom: 70px;
  left: 20px;
  transform-origin: left bottom;
  transform: rotate(90deg);
  transition: transform 1s ease-in-out;
  ${({ rocketLaunched }) =>
    rocketLaunched &&
    `
    transform: rotate(150deg);
  `}
`;

const RocketLaunch = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50px;
  transition: bottom 5s ease-in-out;
  ${({ rocketLaunched }) =>
    rocketLaunched &&
    `
    bottom: 500px;
  `}
`;

const RocketBody = styled.div`
  width: 40px;
  height: 120px;
  background: linear-gradient(to right, #555, #888);
  position: relative;
  border-radius: 10px 10px 0 0;
`;

const RocketNose = styled.div`
  width: 0;
  height: 0;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;
  border-bottom: 40px solid #888;
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
`;

const RocketMiddle = styled.div`
  width: 40px;
  height: 80px;
  background: linear-gradient(to right, #555, #888);
  position: absolute;
  top: 0;
`;

const RocketEngine = styled.div`
  width: 40px;
  height: 20px;
  background: linear-gradient(to right, #888, #aaa);
  position: absolute;
  bottom: -20px;
  border-radius: 0 0 10px 10px;
`;

const Fire = styled.div`
  width: 30px;
  height: 50px;
  background-color: #ff9800;
  position: absolute;
  bottom: -70px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 0 0 50% 50%;
  opacity: 1;
  animation: ${() => fireAnimation} 0.5s infinite alternate;
`;

const fireAnimation = keyframes`
  from {
    transform: translateX(-50%) scale(1);
  }
  to {
    transform: translateX(-50%) scale(1.2);
  }
`;

const ResetButton = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
`;

const RightFrontFins = styled.div`
  width: 0;
  height: 0;
  border-left: 0px solid transparent;
  border-right: 20px solid transparent;
  border-bottom: 30px solid #888;
  position: absolute;
  top: 10px;
  left: 50px;
  transform: translateX(-50%);
`;

const RightRearFins = styled.div`
  width: 0;
  height: 0;
  border-left: 0px solid transparent;
  border-right: 30px solid transparent;
  border-bottom: 40px solid #888;
  position: absolute;
  bottom: 0px;
  left: 55px;
  transform: translateX(-50%);
`;

const LeftFrontFins = styled.div`
  width: 0;
  height: 0;
  border-left: 20px solid transparent;
  border-right: 0px solid transparent;
  border-bottom: 30px solid #888;
  position: absolute;
  top: 10px;
  left: -10px;
  transform: translateX(-50%);
`;

const LeftRearFins = styled.div`
  width: 0;
  height: 0;
  border-left: 30px solid transparent;
  border-right: 0px solid transparent;
  border-bottom: 40px solid #888;
  position: absolute;
  bottom: 0px;
  left: -15px;
  transform: translateX(-50%);
`;

export default RocketCountdownGame;